import { useCallback, useId, useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { clsx } from 'clsx';

type AccordionProps = {
  renderHeader: () => ReactNode;
  renderFooter?: () => ReactNode;
  defaultOpen: boolean;
  canClose?: boolean;
  children: ReactNode;
};

export const Accordion = ({
  renderHeader,
  renderFooter,
  defaultOpen,
  canClose = true,
  children,
}: AccordionProps) => {
  const generatedId = useId();
  const triggerId = `${generatedId}-trigger`;
  const panelId = `${generatedId}-panel`;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [expanded, setExpanded] = useState(defaultOpen);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev && !canClose) return prev;
      if (prev) setExpanded(false);
      return !prev;
    });
  }, [canClose]);

  const handleTransitionEnd = useCallback(() => {
    if (isOpen) setExpanded(true);
  }, [isOpen]);

  return (
    <div
      className={clsx(
        'bg-white rounded-xl',
        expanded ? 'overflow-visible' : 'overflow-hidden',
      )}
    >
      <button
        type="button"
        id={triggerId}
        className="flex items-center justify-between w-full bg-transparent border-none cursor-pointer text-left gap-4 p-4"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={toggle}
      >
        <div className="flex items-center gap-4 flex-1">{renderHeader()}</div>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={clsx(
            'text-[#646464] text-xs transition-transform duration-200 ease-out shrink-0',
            isOpen ? 'rotate-180' : 'rotate-0',
          )}
        />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        aria-hidden={!isOpen}
        onTransitionEnd={handleTransitionEnd}
        className={clsx(
          'grid transition-[grid-template-rows] duration-[250ms] ease-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div
          className={clsx(
            'px-4 min-h-0',
            expanded ? 'overflow-visible' : 'overflow-hidden',
          )}
        >
          <div className="h-[1px] bg-[#eee]" />
          <div className="pt-6">{children}</div>
          {isOpen && renderFooter ? (
            <>
              <div className="h-[1px] bg-[#eee] mt-4" />
              {renderFooter()}
            </>
          ) : null}
          <div className="pb-4" />
        </div>
      </div>
    </div>
  );
};
